/**
 * Tells the Back handler that a file picker is on screen.
 *
 * Android delivers a Back event on the way out of the camera and the file
 * chooser, whether the user picked something or cancelled. The app has a
 * history entry pushed for the open conversation, so that Back consumed it and
 * closed the thread — and closing the thread unmounts the `<input>` whose
 * `change` event was about to carry the photo, so the picture was lost too. One
 * cause, two symptoms: thrown out of the conversation, and nothing sent.
 *
 * A flag rather than a timer: "is a picker open" is a fact we can know exactly,
 * and a race that guesses wrong here either eats a real Back press or drops a
 * photo. The picker is open from the moment we call `click()` until the input
 * reports `change` or `cancel`.
 *
 * `cancel` is not universally supported, so the window regaining focus clears
 * the flag as a backstop. That fires after the picker closes in every browser
 * tested, and clearing late is safe — the flag only ever suppresses a Back that
 * arrives while a picker is genuinely open.
 */
let openPickers = 0;

export function pickerOpened(): void {
  openPickers += 1;
}

export function pickerClosed(): void {
  openPickers = Math.max(0, openPickers - 1);
}

export function pickerIsOpen(): boolean {
  return openPickers > 0;
}

/** Belt and braces for browsers without a `cancel` event on file inputs. */
export function clearPickers(): void {
  openPickers = 0;
}
